import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import BaseStore from '@context/global/mobx/BaseStore';
import { PositionType, TOrderPaymentDetail, TUserOrderType, TUserSimpleOrderType } from '@features/userPanel/typescript';
import customerApi from '@features/api/nextApiService/nextCustomerApi';
import useOrderAdditionalData from '@hooks/useOrderAdditionalData';
class UserPanelOrdersStore extends BaseStore {
  userOrders = observable(new Array<TUserSimpleOrderType>());
  selectedOrder: TUserOrderType | null = null;
  isLoading = false;
  paymentDetails: TOrderPaymentDetail | null = null;
  note = '';
  constructor(customerId: string) {
    super();
    makeObservable(this, {
      userOrders: observable,
      isLoading: observable,
      selectedOrder: observable,
      resetSelectedOrder: action,
      loadOrders: action,
      getOrderDetails: action,
      orderedPositions: computed,
      paymentDetails: observable
    });
    this.loadOrders(customerId);
  }
  loadOrders = this.withProgress(async (customerID: string) => {
    this.isLoading = true;
    const responseUserOrders = await customerApi.getOrdersCollection(customerID);
    const {
      data
    } = await responseUserOrders;
    runInAction(() => {
      this.isLoading = false;
      if (data) {
        this.userOrders.replace(data.data);
      }
    });
    return this.userOrders;
  });
  getOrderDetails = this.withProgress(async (customerID: string, order: TUserSimpleOrderType) => {
    this.isLoading = true;
    const responseUserOrder = await customerApi.getOrderDetails(customerID, order.orderId);
    const {
      data
    } = await responseUserOrder;
    const payment = await this.getOrderPaymentDetails(customerID, order.orderId);
    const noteResponse = await this.getOrderNotes(customerID, order.orderId);
    if (data.invoiceAddress) {
      // biome-ignore lint/correctness/useHookAtTopLevel: MobX store helper
      const {
        getInvoiceAddressWithAdditionalData
      } = useOrderAdditionalData();
      data.invoiceAddress = await getInvoiceAddressWithAdditionalData(data.invoiceAddress, 'taxRegime');
    }
    if (noteResponse?.content) {
      this.note = noteResponse?.content;
      if (data) data.note = noteResponse?.content;
    }
    runInAction(() => {
      this.isLoading = false;
      if (data) {
        this.selectedOrder = data;
      }
      if (payment) {
        this.paymentDetails = payment;
      }
    });
    return this.userOrders;
  });
  getOrderPaymentDetails = async (customerId: string, orderId: string) => {
    const {
      data
    } = await customerApi.getOrderPaymentDetails(customerId, orderId);
    return data;
  };
  getOrderNotes = async (customerId: string, orderId: string) => {
    const {
      data
    } = await customerApi.getOrderNote(customerId, orderId);
    return data;
  };
  get orderedPositions(): Array<PositionType> | [] {
    if (!this.selectedOrder) {
      return [];
    }
    const {
      positions
    } = this.selectedOrder;
    return positions.filter((position: PositionType) => position.type === 'product');
  }
  get orderedAddresses(): Array<PositionType> | [] {
    if (!this.selectedOrder) {
      return [];
    }
    const {
      positions
    } = this.selectedOrder;
    return positions.filter((position: PositionType) => position.type === 'product');
  }
  resetSelectedOrder = action(() => {
    this.selectedOrder = null;
    this.paymentDetails = null;
  });
}
export default UserPanelOrdersStore;